/**
 * useSuccessMeasures Hook
 *
 * React Query hooks for success measure operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  successMeasuresService,
  CreateSuccessMeasureDto,
  UpdateSuccessMeasureDto,
} from '../services/success-measures.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

import type { SuccessMeasure } from '@medsuite/models';

export const successMeasureKeys = {
  all: ['success-measures'] as const,
  lists: () => [...successMeasureKeys.all, 'list'] as const,
  list: (investmentId: number) => [...successMeasureKeys.lists(), investmentId] as const,
};

/**
 * Fetch all success measures for an investment
 */
export function useSuccessMeasures(
  investmentId: number,
  options?: Omit<UseQueryOptions<SuccessMeasure[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<SuccessMeasure[], Error>({
    queryKey: successMeasureKeys.list(investmentId),
    queryFn: async () => {
      const response = await successMeasuresService.getAll(investmentId);
      // Extract data from success wrapper: {success: true, data: {investment_id, success_measures, total}, meta: {}}
      const responseData = unwrapResponse<{ success_measures?: SuccessMeasure[] }>(response.data);
      // The API returns {investment_id, success_measures, total}, extract the success_measures array
      return responseData.success_measures || [];
    },
    ...options,
  });
}

/**
 * Create new success measure
 */
export function useCreateSuccessMeasure(
  investmentId: number,
  options?: UseMutationOptions<SuccessMeasure, Error, CreateSuccessMeasureDto>
) {
  const queryClient = useQueryClient();

  return useMutation<SuccessMeasure, Error, CreateSuccessMeasureDto>({
    mutationFn: async (data) => {
      const response = await successMeasuresService.create(investmentId, data);
      return unwrapResponse<SuccessMeasure>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: successMeasureKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Update success measure
 */
export function useUpdateSuccessMeasure(
  investmentId: number,
  options?: UseMutationOptions<
    SuccessMeasure,
    Error,
    { successMeasureId: number; data: UpdateSuccessMeasureDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    SuccessMeasure,
    Error,
    { successMeasureId: number; data: UpdateSuccessMeasureDto }
  >({
    mutationFn: async ({ successMeasureId, data }) => {
      const response = await successMeasuresService.update(investmentId, successMeasureId, data);
      return unwrapResponse<SuccessMeasure>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: successMeasureKeys.list(investmentId) });
    },
    ...options,
  });
}

/**
 * Delete success measure
 */
export function useDeleteSuccessMeasure(
  investmentId: number,
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (successMeasureId) => {
      await successMeasuresService.delete(investmentId, successMeasureId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: successMeasureKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}
